import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-emerald-600" />
            <span className="font-semibold text-slate-800">CurrículoPRO</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-slate-800 mb-8">Política de Privacidade</h1>
        
        <div className="prose prose-slate max-w-none space-y-6">
          <p className="text-slate-600">
            <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR')}
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">1. Informações Gerais</h2>
            <p className="text-slate-600">
              A sua privacidade é importante para nós. Esta Política de Privacidade explica como o CurrículoPRO 
              coleta, usa e protege suas informações quando você utiliza nosso serviço de criação de currículos.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">2. Coleta de Dados</h2>
            <p className="text-slate-600">
              O CurrículoPRO é um serviço que funciona 100% no seu navegador. Isso significa que:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Seus dados pessoais (nome, email, telefone, experiências) são armazenados <strong>apenas no seu dispositivo</strong> através do localStorage do navegador.</li>
              <li><strong>Não enviamos</strong> seus dados pessoais para nenhum servidor externo.</li>
              <li><strong>Não temos acesso</strong> às informações do seu currículo.</li>
              <li>Você pode excluir todos os seus dados a qualquer momento clicando em "Limpar" no editor.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">3. Cookies e Tecnologias de Rastreamento</h2>
            <p className="text-slate-600">
              Utilizamos cookies e tecnologias similares para:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Cookies essenciais:</strong> Para salvar suas preferências de consentimento e funcionamento do site.</li>
              <li><strong>Cookies de análise:</strong> Para entender como os visitantes utilizam o site.</li>
              <li><strong>Cookies de publicidade:</strong> Utilizamos o Google AdSense para exibir anúncios. O Google pode usar cookies para personalizar anúncios com base em suas visitas a este e outros sites.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">4. Google AdSense</h2>
            <p className="text-slate-600">
              Este site utiliza o Google AdSense para exibir anúncios. O Google, como fornecedor terceirizado, 
              utiliza cookies para veicular anúncios no nosso site. O uso do cookie DART pelo Google permite 
              que ele veicule anúncios para os usuários com base nas visitas a este site e a outros sites na Internet.
            </p>
            <p className="text-slate-600">
              Você pode desativar o uso do cookie DART visitando a{' '}
              <a 
                href="https://policies.google.com/technologies/ads" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-600 hover:underline"
              >
                página de políticas de privacidade do Google
              </a>.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">5. Seus Direitos (LGPD)</h2>
            <p className="text-slate-600">
              De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Confirmar a existência de tratamento de dados</li>
              <li>Acessar seus dados</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
              <li>Solicitar a anonimização, bloqueio ou eliminação de dados</li>
              <li>Revogar o consentimento</li>
            </ul>
            <p className="text-slate-600">
              Como seus dados de currículo são armazenados apenas localmente no seu dispositivo, 
              você tem controle total sobre eles e pode excluí-los a qualquer momento.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">6. Segurança</h2>
            <p className="text-slate-600">
              Implementamos medidas de segurança apropriadas para proteger contra acesso não autorizado, 
              alteração, divulgação ou destruição de dados. Como os dados do currículo ficam apenas no seu 
              dispositivo, a segurança desses dados depende da segurança do seu próprio dispositivo e navegador.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">7. Alterações nesta Política</h2>
            <p className="text-slate-600">
              Podemos atualizar esta Política de Privacidade periodicamente. Recomendamos que você revise 
              esta página regularmente para se manter informado sobre quaisquer mudanças.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-slate-800">8. Contato</h2>
            <p className="text-slate-600">
              Se você tiver dúvidas sobre esta Política de Privacidade, entre em contato conosco através 
              do canal{' '}
              <a 
                href="https://www.youtube.com/@godgamestutorial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-emerald-600 hover:underline"
              >
                God Games Tutorial
              </a>.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-400 py-6 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm">
            Criado por{' '}
            <a
              href="https://www.youtube.com/@godgamestutorial"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              God Games Tutorial
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
